import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addActivity,
  addContactActivity,
  deleteActivity,
  deleteContactActivity,
  listActivities,
  listContactActivities,
  pinContactActivity,
  pinDealActivity,
  updateActivity,
  updateContactActivity,
} from '@/api/activities'
import { listDealTasks } from '@/api/tasks'

export function useDealActivities(dealId: string) {
  return useQuery({ queryKey: ['activities', dealId], queryFn: () => listActivities(dealId) })
}

export function useDealTasks(dealId: string) {
  return useQuery({ queryKey: ['tasks', 'deal', dealId], queryFn: () => listDealTasks(dealId) })
}

export function usePinDealActivity(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ activityId, pinned }: { activityId: string; pinned: boolean }) =>
      pinDealActivity(dealId, activityId, pinned),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activities', dealId] }),
  })
}

export function usePinContactActivity(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ activityId, pinned }: { activityId: string; pinned: boolean }) =>
      pinContactActivity(contactId, activityId, pinned),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-activities', contactId] }),
  })
}

export function useAddActivity(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { activityType: string; body: string }) => addActivity(dealId, params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities', dealId] })
      qc.invalidateQueries({ queryKey: ['deal', dealId] }) // lastContactedAt may change
      qc.invalidateQueries({ queryKey: ['attention'] }) // staleness reset affects ranking
    },
  })
}

export function useUpdateActivity(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { activityId: string; activityType?: string; body: string }) =>
      updateActivity(dealId, params.activityId, { activityType: params.activityType, body: params.body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activities', dealId] }),
  })
}

export function useDeleteActivity(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (activityId: string) => deleteActivity(dealId, activityId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activities', dealId] }),
  })
}

export function useContactActivities(contactId: string) {
  return useQuery({
    queryKey: ['contact-activities', contactId],
    queryFn: () => listContactActivities(contactId),
  })
}

export function useAddContactActivity(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { activityType: string; body: string }) => addContactActivity(contactId, params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-activities', contactId] })
      qc.invalidateQueries({ queryKey: ['contact', contactId] })
    },
  })
}

export function useUpdateContactActivity(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { activityId: string; activityType?: string; body: string }) =>
      updateContactActivity(contactId, params.activityId, {
        activityType: params.activityType,
        body: params.body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-activities', contactId] }),
  })
}

export function useDeleteContactActivity(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (activityId: string) => deleteContactActivity(contactId, activityId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-activities', contactId] }),
  })
}
